import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Badge } from "antd";
import { AnimatePresence, motion } from "framer-motion";
import {
  Bell,
  BookOpen,
  CalendarDays,
  CheckCheck,
  ChevronDown,
  Home,
  LogOut,
  Menu,
  Phone,
  ShieldCheck,
  Sparkles,
  Trash2,
  UserRound,
  X,
} from "lucide-react";
import Axios from "../../services/AxiosInterceptor";

const navLinks = [
  { label: "Home", to: "/", icon: Home },
  { label: "Events", to: "/Events", icon: CalendarDays },
  { label: "Contact", to: "/contact_us", icon: Phone },
];

const serviceLinks = [
  { label: "Personal Yoga", to: "/Get_Start" },
  { label: "Group Yoga", to: "/Get_Start" },
  { label: "Free Demo", to: "/demo-services" },
];

const panelMotion = {
  initial: { opacity: 0, y: -8, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -8, scale: 0.98 },
  transition: { duration: 0.18 },
};

const CustomerHeader = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [role, setRole] = useState({ rolee: "", emaile: "", userId: "" });
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isCustomer = role?.rolee === "Customer";

  const sync = () => {
    const Role = localStorage.getItem("role") || "";
    const email = localStorage.getItem("Username") || localStorage.getItem("email") || "";
    const userId = localStorage.getItem("userId") || "";
    setRole({ rolee: Role, emaile: email, userId });
  };

  useEffect(() => {
    sync();

    const onStorage = (e) => {
      if (!e.key || ["role", "email", "Username", "userId"].includes(e.key)) sync();
    };
    const onAuth = () => sync();
    const onFocus = () => sync();

    window.addEventListener("storage", onStorage);
    window.addEventListener("yogamatic:auth", onAuth);
    window.addEventListener("focus", onFocus);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("yogamatic:auth", onAuth);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  useEffect(() => {
    sync();
    setIsOpen(false);
    setServicesOpen(false);
    setShowNotifications(false);
  }, [location.pathname]);

  const fetchNotifications = async () => {
    if (!role.userId) return;

    try {
      const res = await Axios.get(`/api/notifications/${role.userId}`);
      const data = res.data;

      if (data.success) {
        const nextNotifications = data.data || [];
        const unread = nextNotifications.filter((n) => !n.isRead).length;

        if (unread > unreadCount && navigator.vibrate) navigator.vibrate(200);

        setNotifications(nextNotifications);
        setUnreadCount(unread);
      }
    } catch (err) {
      console.error("Error fetching notifications", err);
    }
  };

  useEffect(() => {
    if (isCustomer && role.userId) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 60000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [isCustomer, role.userId]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 1024) setIsOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setServicesOpen(false);
        setShowNotifications(false);
      }
    };

    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const handleNav = (to) => {
    setIsOpen(false);
    setServicesOpen(false);
    setShowNotifications(false);
    if (to) navigate(to);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("email");
    localStorage.removeItem("Username");
    localStorage.removeItem("userId");
    localStorage.removeItem("mobile");
    window.dispatchEvent(new Event("yogamatic:auth"));
    navigate("/");
  };

  const markAllRead = async () => {
    if (!role.userId) return;

    try {
      await Axios.put(`/api/notifications/mark-read/${role.userId}`);
      setNotifications((items) => items.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Error marking notifications as read", err);
    }
  };

  const deleteNotification = async (id, e) => {
    e.stopPropagation();

    try {
      await Axios.delete(`/api/notifications/${id}`);
      setNotifications((items) => {
        const updated = items.filter((n) => n._id !== id);
        setUnreadCount(updated.filter((n) => !n.isRead).length);
        return updated;
      });
    } catch (err) {
      console.error("Error deleting notification", err);
    }
  };

  const handleNotificationClick = (n) => {
    if (["booking_confirmed", "payment_approved", "payment_rejected"].includes(n.type)) {
      navigate("/My_Bookings");
    }
    setShowNotifications(false);
  };

  const accountLabel = useMemo(() => {
    if (!role.emaile) return "My account";
    return role.emaile.length > 18 ? `${role.emaile.slice(0, 18)}...` : role.emaile;
  }, [role.emaile]);

  const NavLink = ({ item }) => {
    const Icon = item.icon;
    const active = location.pathname === item.to;
    return (
      <button
        type="button"
        className={`ym-nav-link ${active ? "active" : ""}`}
        onClick={() => handleNav(item.to)}
      >
        <Icon size={16} />
        {item.label}
      </button>
    );
  };

  return (
    <header className="ym-header" role="banner">
      <div className="ym-header-wrap">
        <button type="button" className="ym-brand" onClick={() => handleNav("/")} aria-label="Go to home">
          <img src="/yogamaticicon.jpg" className="ym-logo" alt="YogaMatic" />
          <span className="ym-brand-text">
            <strong>YogaMatic</strong>
            <span>Jaipur Yoga Studio</span>
          </span>
        </button>

        <nav className="ym-nav" aria-label="Main navigation">
          <div className="ym-nav-group">
            {navLinks.slice(0, 1).map((item) => (
              <NavLink item={item} key={item.to} />
            ))}

            <div
              className="ym-dropdown-wrap"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                type="button"
                className="ym-nav-button"
                onClick={() => setServicesOpen((value) => !value)}
                aria-expanded={servicesOpen}
              >
                <Sparkles size={16} />
                Services
                <ChevronDown size={15} />
              </button>

              <AnimatePresence>
                {servicesOpen && (
                  <motion.div className="ym-dropdown" {...panelMotion}>
                    {serviceLinks.map((item) => (
                      <a
                        href={item.to}
                        key={item.label}
                        onClick={(e) => {
                          e.preventDefault();
                          handleNav(item.to);
                        }}
                      >
                        {item.label}
                        <ChevronDown size={14} style={{ transform: "rotate(-90deg)" }} />
                      </a>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {navLinks.slice(1).map((item) => (
              <NavLink item={item} key={item.to} />
            ))}
          </div>

          <div className="ym-nav-group">
            {isCustomer ? (
              <>
                <div className="ym-notification-wrap">
                  <button
                    type="button"
                    className="ym-icon-button"
                    onClick={() => setShowNotifications((value) => !value)}
                    aria-label="Notifications"
                    title="Notifications"
                  >
                    <Badge count={unreadCount} size="small">
                      <Bell size={19} />
                    </Badge>
                  </button>

                  <AnimatePresence>
                    {showNotifications && (
                      <motion.div className="ym-notification-dropdown" {...panelMotion}>
                        <div className="ym-notification-head">
                          <strong>Notifications</strong>
                          <button type="button" className="ym-nav-button" onClick={markAllRead}>
                            <CheckCheck size={16} />
                            Mark read
                          </button>
                        </div>
                        <ul className="ym-notification-list">
                          {notifications.length > 0 ? (
                            notifications.map((n) => (
                              <li
                                key={n._id}
                                className={`ym-notification-item ${!n.isRead ? "unread" : ""}`}
                                onClick={() => handleNotificationClick(n)}
                              >
                                <span>
                                  <span className="ym-notification-title">{n.title}</span>
                                  <span className="ym-notification-message">{n.message}</span>
                                  <span className="ym-notification-date">
                                    {n.createdAt ? new Date(n.createdAt).toLocaleString() : ""}
                                  </span>
                                </span>
                                <button
                                  type="button"
                                  className="ym-icon-button"
                                  onClick={(e) => deleteNotification(n._id, e)}
                                  aria-label="Remove notification"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </li>
                            ))
                          ) : (
                            <li className="ym-empty">No notifications yet</li>
                          )}
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <button type="button" className="ym-nav-button ym-nav-outline" onClick={() => handleNav("/My_Bookings")}>
                  <BookOpen size={16} />
                  My Bookings
                </button>
                <span className="ym-nav-button" aria-label="Signed in user">
                  <UserRound size={16} />
                  {accountLabel}
                </span>
                <button type="button" className="ym-nav-button" onClick={logout}>
                  <LogOut size={16} />
                  Logout
                </button>
              </>
            ) : (
              <>
                <button type="button" className="ym-nav-button ym-nav-cta" onClick={() => handleNav("/cx_Login")}>
                  <UserRound size={16} />
                  Sign Up / Login
                </button>
                <button type="button" className="ym-nav-button ym-nav-outline" onClick={() => handleNav("/admin")}>
                  <ShieldCheck size={16} />
                  Admin
                </button>
              </>
            )}
          </div>
        </nav>

        <button
          type="button"
          className="ym-icon-button ym-menu-button"
          onClick={() => setIsOpen((value) => !value)}
          aria-expanded={isOpen}
          aria-label={isOpen ? "Close menu" : "Open menu"}
        >
          {isOpen ? <X size={22} /> : <Menu size={22} />}
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div className="ym-mobile-panel" {...panelMotion}>
              {navLinks.map((item) => (
                <NavLink item={item} key={item.to} />
              ))}

              {serviceLinks.map((item) => (
                <button
                  type="button"
                  key={item.label}
                  className="ym-nav-link"
                  onClick={() => handleNav(item.to)}
                >
                  <Sparkles size={16} />
                  {item.label}
                </button>
              ))}

              {isCustomer ? (
                <>
                  <button type="button" className="ym-nav-link" onClick={() => handleNav("/My_Bookings")}>
                    <BookOpen size={16} />
                    My Bookings
                  </button>
                  <button type="button" className="ym-nav-link" onClick={logout}>
                    <LogOut size={16} />
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="ym-nav-link ym-nav-cta" onClick={() => handleNav("/cx_Login")}>
                    <UserRound size={16} />
                    Sign Up / Login
                  </button>
                  <button type="button" className="ym-nav-link" onClick={() => handleNav("/admin")}>
                    <ShieldCheck size={16} />
                    Admin Login
                  </button>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default CustomerHeader;
